// This component is workable to get Rapid7 and Tenable Information.

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { APIService, GetEntityQuery } from 'app/API.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EntityService } from 'app/shared/entity.service';
import { ToastrService } from 'ngx-toastr';
import { Services, ServiceName } from '../../constants';
import { Azure, Integration, AWS_TITLE, QUALYS_PRODUCTS } from 'app/shared/enums/integration.enum';
import { UtilsService } from '../utils.service';
import { CURR_VIEW } from 'app/shared/enums/shared.enum';
import { CollectionService } from 'app/collection/collection.service';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { FileService } from '../file.service';
import saveAs from 'file-saver';

interface IntegrationModal {
  name: string;
  credentials?: string;
  isEnable?: boolean;
  isAuth?: boolean;
  inheritFromParent?: boolean;
}

interface IntegrationCreds {
  url?: string;
  username?: string;
  password?: string;
  accessKey?: string;
  secretKey?: string;
  currView?: View;
  tenantId?: string;
  clientSecret?: string;
  subscriptionId?: string;
  clientId?: string;
  roleArn?: string;
  region?: string;
  product?: string;
  apiToken?: string;
  orgId?: string;
  resourceGroupName?: string;
  workspaceName?: string;
  apiEndpointUrl?: string;
  bearerToken?: string;
  apiHostname?: string;
  integrationKey?: string;
  token?: string;
  organization?: string;
  apiId?: string;
  apiKey?: string;
  serviceAccountKey?: string;
  organizationId?: string;
}

type View = 'accessKey' | 'username' | 'token' | null;

const currViewMap = {
  tenable: CURR_VIEW.ACCESSKEY,
  nessus: CURR_VIEW.ACCESSKEY,
  rapid7: CURR_VIEW.USERNAME,
  splunk: CURR_VIEW.USERNAME,
};

@Component({
  selector: 'cygov-scan-integration-service-modal',
  templateUrl: './scan-integration-service-modal.component.html',
  styleUrls: ['./scan-integration-service-modal.component.scss'],
  standalone: false,
})
export class ScanIntegrationServiceModalComponent implements OnInit {
  @Input() entityId: string;
  @Input() isTenable: boolean;
  @Input() serviceName: string;
  @Output() accountEntity: EventEmitter<any> = new EventEmitter();
  entity: GetEntityQuery;
  formData: FormGroup;
  serviceType: string;
  formDisabled = false;
  integrations: IntegrationModal[] = [];
  r7Placeholder: string = 'https://rapid7.com/api/3';
  tenablePlaceholder: string = 'https://sc.tenalab.online/rest';
  nessusPlaceholder: string = 'https://nessus-server:8834';
  crowdstrikePlaceholder: string = 'api.us-2.crowdstrike.com';
  placeholder: string = this.tenablePlaceholder; // by default

  // tenable var
  currView: View = CURR_VIEW.ACCESSKEY;
  Integration = Integration; // enum;
  title: string;
  qualysPlaceholder: string = 'https://gateway.qg1.apps.qualys.com';
  qualysVMDRPlaceholder: string = 'https://qualysapi.qg1.apps.qualys.com';
  prismacloudPlaceholder: string = 'https://api.prismacloud.io';
  qualysProducts = [
    { value: 'VMDR', label: 'VMDR (Vulnerability Management, Detection and Response)' },
    { value: 'ETM', label: 'ETM (Enterprise Threat Management)' },
  ];
  downloadGuideInProgress: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private entityService: EntityService,
    private toastrService: ToastrService,
    private collectionService: CollectionService,
    private customApi: CUSTOMAPIService,
    private fileService: FileService
  ) {}

  async ngOnInit(): Promise<any> {
    this.entity = await this.getEntity();
    this.serviceType = Services[this.serviceName.toUpperCase()];
    let credentials: IntegrationCreds = { url: null, username: null, password: null, product: null };

    switch (this.serviceName) {
      case Integration.AZURE:
        this.title = Azure.AZURE;
        break;
      case Integration.MICROSOFT:
        this.title = Azure.MICROSOFT;
        break;
      case Integration.AWSSECURITYHUB:
        this.title = AWS_TITLE.AWS;
        break;
      case Integration.INTUNE:
        this.title = 'Microsoft Intune';
        break;
      case Integration.QUALYSETM:
        this.title = QUALYS_PRODUCTS.ETM;
        break;
      case Integration.QUALYSVMDR:
        this.title = QUALYS_PRODUCTS.VMDR;
        break;
      case Integration.SNYK:
        this.title = 'Snyk';
        break;
      case Integration.SENTINEL:
        this.title = 'Microsoft Sentinel';
        break;
      case Integration.SENTINELONE:
        this.title = 'SentinelOne';
        break;
      case Integration.WIZ:
        this.title = 'Wiz';
        break;
      case Integration.OKTA:
        this.title = 'Okta';
        break;
      case Integration.ORCA:
        this.title = 'Orca Security';
        break;
      case Integration.PRISMACLOUD:
        this.title = 'Prisma Cloud';
        break;
      case Integration.DUO:
        this.title = 'Duo';
        break;
      case Integration.GITHUBSECURITY:
        this.title = 'GitHub Advanced Security';
        break;
      case Integration.GITLAB:
        this.title = 'GitLab';
        break;
      case Integration.VERACODE:
        this.title = 'Veracode';
        break;
      case Integration.SONARQUBE:
        this.title = 'SonarQube';
        break;
      case Integration.KNOWBE4:
        this.title = 'KnowBe4';
        break;
      case Integration.GCPSCC:
        this.title = 'Google Cloud SCC';
        break;
      case Integration.SERVICENOWCMDB:
        this.title = 'ServiceNow CMDB';
        break;
      case Integration.CYBERARK:
        this.title = 'CyberArk';
        break;

      default:
        this.title = this.serviceName;
        break;
    }

    this.setPlaceholder(this.serviceName);
    this.currView = currViewMap[this.serviceName] ? currViewMap[this.serviceName] : CURR_VIEW.USERNAME;

    if (this.entity?.integrations) {
      // call lambda to decrypt the credentials and sent back to frontend
      const payload = {
        id: this.entityId,
        service: this.serviceType,
        type: 'Query',
        source: 'frontend',
      };

      try {
        const integration = await this.customApi.HandleIntegrationDetails(JSON.stringify(payload));
        const integrationReadable = await integration.body.json();
        console.log('Updated Integration:', integrationReadable);
        if (integrationReadable && integrationReadable.data) {
          const integrationData = integrationReadable.data;

          credentials = JSON.parse(integrationData.credentials);
          if (credentials?.currView) {
            this.currView = credentials.currView;
          }

          const idx = this.entity.integrations.findIndex(int => int.name === integrationData.name);
          this.entity.integrations[idx].credentials = integrationData.credentials;
        }
      } catch (e) {
        console.log('Issue in integrat', e);
      }
    }

    switch (this.serviceName) {
      case Integration.AZURE:
      case Integration.MICROSOFT:
        this.formData = new FormGroup({
          // eslint-disable-next-line @typescript-eslint/unbound-method
          subscriptionId: new FormControl(credentials.subscriptionId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          tenantId: new FormControl(credentials.tenantId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientId: new FormControl(credentials.clientId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientSecret: new FormControl(credentials.clientSecret),
        });
        break;
      case Integration.AWSSECURITYHUB:
        // roleArn: 'arn:aws:iam::221306060527:role/SecurityHubRole',
        // region: process.env.REGION,
        // rootId: '6e7a2c18-3816-447f-aafa-8f2f1fd556e2',
        this.formData = new FormGroup({
          // eslint-disable-next-line @typescript-eslint/unbound-method
          roleArn: new FormControl(credentials.roleArn),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          region: new FormControl(credentials.region),
        });
        break;
      case Integration.SNYK:
        this.formData = new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          orgId: new FormControl(credentials.orgId),
          url: new FormControl(credentials.url),
        });
        break;
      case Integration.INTUNE:
        this.formData = new FormGroup({
          tenantId: new FormControl(credentials.tenantId),
          clientId: new FormControl(credentials.clientId),
          clientSecret: new FormControl(credentials.clientSecret),
        });
        break;
      case Integration.SENTINEL:
        this.formData = new FormGroup({
          // eslint-disable-next-line @typescript-eslint/unbound-method
          tenantId: new FormControl(credentials.tenantId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          subscriptionId: new FormControl(credentials.subscriptionId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientId: new FormControl(credentials.clientId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientSecret: new FormControl(credentials.clientSecret),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          resourceGroupName: new FormControl(credentials.resourceGroupName),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          workspaceName: new FormControl(credentials.workspaceName),
        });
        break;
      case Integration.SENTINELONE:
        this.formData = new FormGroup({
          url: new FormControl(credentials.url),
          apiToken: new FormControl(credentials.apiToken),
        });
        break;
      case Integration.WIZ:
        this.formData = new FormGroup({
          // eslint-disable-next-line @typescript-eslint/unbound-method
          apiEndpointUrl: new FormControl(credentials.apiEndpointUrl),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientId: new FormControl(credentials.clientId),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          clientSecret: new FormControl(credentials.clientSecret),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          resourceGroupName: new FormControl(credentials.resourceGroupName),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          workspaceName: new FormControl(credentials.workspaceName),
        });
        break;
      case Integration.SPLUNK:
        this.formData = new FormGroup({
          url: new FormControl(credentials.url),
          username: new FormControl(credentials.username),
          password: new FormControl(credentials.password),
          bearerToken: new FormControl(credentials.bearerToken),
        });
        break;
      case Integration.OKTA:
        this.formData = new FormGroup({
          // eslint-disable-next-line @typescript-eslint/unbound-method
          url: new FormControl(credentials.url),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          apiToken: new FormControl(credentials.apiToken),
        });
        break;
      case Integration.ORCA:
        this.formData = new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          url: new FormControl(credentials.url),
        });
        break;
      case Integration.DUO:
        this.formData = new FormGroup({
          apiHostname: new FormControl(credentials.apiHostname),
          integrationKey: new FormControl(credentials.integrationKey),
          secretKey: new FormControl(credentials.secretKey),
        });
        break;
      case Integration.GITHUBSECURITY:
        this.formData = new FormGroup({
          token: new FormControl(credentials.token),
          organization: new FormControl(credentials.organization),
        });
        break;
      case Integration.GITLAB:
        this.formData = new FormGroup({
          token: new FormControl(credentials.token),
          url: new FormControl(credentials.url),
        });
        break;
      case Integration.VERACODE:
        this.formData = new FormGroup({
          url: new FormControl(credentials.url),
          apiId: new FormControl(credentials.apiId),
          apiKey: new FormControl(credentials.apiKey),
        });
        break;
      case Integration.SONARQUBE:
        this.formData = new FormGroup({
          url: new FormControl(credentials.url),
          token: new FormControl(credentials.token),
        });
        break;
      case Integration.KNOWBE4:
        this.formData = new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          region: new FormControl(credentials.region || 'us'),
        });
        break;
      case Integration.GCPSCC:
        this.formData = new FormGroup({
          serviceAccountKey: new FormControl(credentials.serviceAccountKey),
          organizationId: new FormControl(credentials.organizationId),
        });
        break;
      case Integration.SERVICENOWCMDB:
        this.formData = new FormGroup({
          url: new FormControl(credentials.url),
          username: new FormControl(credentials.username),
          password: new FormControl(credentials.password),
        });
        break;
      default:
        const defaultFormControls: any = {
          // eslint-disable-next-line @typescript-eslint/unbound-method
          url: new FormControl(credentials.url),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          username: new FormControl(credentials.username),
          // eslint-disable-next-line @typescript-eslint/unbound-method
          password: new FormControl(credentials.password),

          // eslint-disable-next-line @typescript-eslint/unbound-method
          accessKey: new FormControl(credentials.accessKey),
          // eslint-disable-next-line @typescript-eslint/unbound-method
          secretKey: new FormControl(credentials.secretKey),
        };

        // Add product selection for Qualys
        if (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) {
          defaultFormControls.product = new FormControl(credentials.product || 'VMDR', [Validators.required]);
        }

        this.formData = new FormGroup(defaultFormControls);
        break;
    }
    // if (this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) {
    //   this.formData = new FormGroup({
    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     subscriptionId: new FormControl(credentials.subscriptionId),

    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     tenantId: new FormControl(credentials.tenantId),

    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     clientId: new FormControl(credentials.clientId),

    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     clientSecret: new FormControl(credentials.clientSecret),
    //   });
    // } else {
    //   this.formData = new FormGroup({
    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     url: new FormControl(credentials.url),

    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     username: new FormControl(credentials.username),
    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     password: new FormControl(credentials.password),

    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     accessKey: new FormControl(credentials.accessKey),
    //     // eslint-disable-next-line @typescript-eslint/unbound-method
    //     secretKey: new FormControl(credentials.secretKey),
    //   });
    // }
  }

  async onSubmit(): Promise<void> {
    this.formDisabled = true;
    const formDataControl = this.formData.controls;

    let integrationArray: IntegrationModal[] = [];
    if (
      ((this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) &&
        this.isDefenderFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.AWSSECURITYHUB && this.isAwsFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SNYK && formDataControl?.apiToken?.value && formDataControl?.orgId?.value) ||
      (this.serviceName === ServiceName.INTUNE && this.isIntuneFormValid(formDataControl)) ||
      (this.serviceName === Integration.SENTINEL && this.isSentinelFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SENTINELONE &&
        formDataControl?.url?.value &&
        formDataControl?.apiToken?.value) ||
      (this.serviceName === ServiceName.WIZ && this.isWizFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SPLUNK && this.isSplunkFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.OKTA && this.isOktaFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.ORCA && this.isOrcaFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.DUO && this.isDuoFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GITHUBSECURITY && this.isGithubSecurityFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GITLAB && this.isGitlabFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.VERACODE && this.isVeracodeFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SONARQUBE && this.isSonarqubeFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.KNOWBE4 && this.isKnowbe4FormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GCPSCC && this.isGcpsccFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SERVICENOWCMDB &&
        formDataControl?.url?.value &&
        formDataControl?.username?.value &&
        formDataControl?.password?.value) ||
      (this.serviceName === ServiceName.CYBERARK &&
        formDataControl?.url?.value &&
        formDataControl?.username?.value &&
        formDataControl?.password?.value) ||
      (formDataControl?.url?.value &&
        ((this.currView === CURR_VIEW.USERNAME &&
          formDataControl?.username?.value &&
          formDataControl?.password?.value) ||
          (this.currView === CURR_VIEW.ACCESSKEY &&
            formDataControl?.accessKey?.value &&
            formDataControl?.secretKey?.value)) &&
        // No additional validation needed for Qualys since product is automatic
        // Additional validation for Qualys product selection
        ((this.serviceName !== Integration.QUALYSETM && this.serviceName !== Integration.QUALYSVMDR) ||
          formDataControl?.product?.value))
    ) {
      let info: IntegrationCreds = {
        url: formDataControl?.url?.value,
        currView: this.currView,
      };

      if (this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) {
        info = {};
        info.subscriptionId = formDataControl.subscriptionId.value;
        info.tenantId = formDataControl.tenantId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === ServiceName.AWSSECURITYHUB) {
        info = {};
        info.roleArn = formDataControl.roleArn.value;
        info.region = formDataControl.region.value;
      } else if (this.serviceName === ServiceName.SNYK) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.orgId = formDataControl.orgId.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.INTUNE) {
        info = {};
        info.tenantId = formDataControl.tenantId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === Integration.SENTINEL) {
        info = {};
        info.tenantId = formDataControl.tenantId.value;
        info.subscriptionId = formDataControl.subscriptionId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
        info.resourceGroupName = formDataControl.resourceGroupName.value;
        info.workspaceName = formDataControl.workspaceName.value;
      } else if (this.serviceName === ServiceName.SENTINELONE) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiToken = formDataControl.apiToken.value;
      } else if (this.serviceName === ServiceName.WIZ) {
        info = {};
        info.apiEndpointUrl = formDataControl.apiEndpointUrl.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === ServiceName.SPLUNK) {
        info = { url: formDataControl.url.value, currView: this.currView };
        if (this.currView === CURR_VIEW.USERNAME) {
          info.username = formDataControl.username.value;
          info.password = formDataControl.password.value;
        } else {
          info.bearerToken = formDataControl.bearerToken.value;
        }
      } else if (this.serviceName === ServiceName.OKTA) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiToken = formDataControl.apiToken.value;
      } else if (this.serviceName === ServiceName.ORCA) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.DUO) {
        info = {};
        info.apiHostname = formDataControl.apiHostname.value;
        info.integrationKey = formDataControl.integrationKey.value;
        info.secretKey = formDataControl.secretKey.value;
      } else if (this.serviceName === ServiceName.GITHUBSECURITY) {
        info = {};
        info.token = formDataControl.token.value;
        info.organization = formDataControl.organization.value;
      } else if (this.serviceName === ServiceName.GITLAB) {
        info = {};
        info.token = formDataControl.token.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.VERACODE) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiId = formDataControl.apiId.value;
        info.apiKey = formDataControl.apiKey.value;
      } else if (this.serviceName === ServiceName.SONARQUBE) {
        info = {};
        info.url = formDataControl.url.value;
        info.token = formDataControl.token.value;
      } else if (this.serviceName === ServiceName.KNOWBE4) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.region = formDataControl.region.value;
      } else if (this.serviceName === ServiceName.GCPSCC) {
        info = {};
        info.serviceAccountKey = formDataControl.serviceAccountKey.value;
        info.organizationId = formDataControl.organizationId.value;
      } else if (this.serviceName === ServiceName.SERVICENOWCMDB) {
        info = {};
        info.url = formDataControl.url.value;
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else if (this.serviceName === ServiceName.CYBERARK) {
        info = {};
        info.url = formDataControl.url.value;
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else if (this.currView === CURR_VIEW.USERNAME) {
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else {
        info.accessKey = formDataControl.accessKey.value;
        info.secretKey = formDataControl.secretKey.value;
      }

      // Add product selection for Qualys
      if (
        (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) &&
        formDataControl?.product?.value
      ) {
        info.product = formDataControl.product.value;
      }
      if (this.entity?.integrations) {
        integrationArray = this.entity.integrations.map(integration => {
          delete integration.__typename;
          return { ...integration };
        });
      }
      let integrationInfo = this.setIntegrationInfo(info);
      let isExistingCredentials: boolean = false;
      if (integrationArray.length === 0) {
        integrationArray = [integrationInfo];
      } else {
        const existingItemIndex = integrationArray.findIndex(elem => elem.name === this.serviceType);

        if (existingItemIndex > -1) {
          const existingIntegrationInfo: IntegrationCreds = JSON.parse(integrationArray[existingItemIndex].credentials);
          this.updateExistingCredentials(existingIntegrationInfo, formDataControl);
          integrationInfo = this.setIntegrationInfo(existingIntegrationInfo);
          integrationArray[existingItemIndex] = integrationInfo;
          isExistingCredentials = true;
        } else {
          this.integrations = [];
          this.integrations.push(integrationInfo);
          integrationArray.push(integrationInfo);
        }
      }

      const payload = {
        id: this.entityId,
        newlyAddedIntegration: integrationInfo,
        type: 'Mutation',
        source: 'frontend',
      };
      const integrationResponse = await this.customApi.HandleIntegrationDetails(JSON.stringify(payload));
      const integrationReadable = await integrationResponse.body.json();
      if (integrationReadable && integrationReadable.data) {
        this.entity = integrationReadable.data;
        // this.entity = JSON.parse(updatedIntegration);

        if (isExistingCredentials) {
          this.collectionService.updateIntegrationTabs$.next(this.entity);
        }
        this.toastrService.success('Information has been saved.');
        this.runGetSites();
      }
    } else {
      this.toastrService.error('Missing required fields');
    }
    this.formDisabled = false;
  }

  runGetSites(): void {
    const eventPayload: any = { entity: this.entity, currService: this.serviceName };
    this.accountEntity.emit(eventPayload);
  }

  setIntegrationInfo(info): IntegrationModal {
    return {
      name: this.serviceType,
      credentials: JSON.stringify(info),
      isEnable: true,
      isAuth: null,
      inheritFromParent: null,
    };
  }

  async getEntity(): Promise<GetEntityQuery> {
    return await this.entityService.getEntity(this.entityId, false);
  }

  closeModal(): void {
    this.activeModal.close();
  }

  setPlaceholder(serviceName: string): void {
    switch (serviceName && serviceName.toLowerCase()) {
      case Integration.TENABLE:
        this.placeholder = this.tenablePlaceholder;
        break;
      case Integration.NESSUS:
        this.placeholder = this.nessusPlaceholder;
        break;
      case Integration.CROWDSTRIKE:
        this.placeholder = this.crowdstrikePlaceholder;
        break;
      case Integration.RAPID7:
        this.placeholder = this.r7Placeholder;
        break;
      case Integration.QUALYSETM:
        this.placeholder = this.qualysPlaceholder;
        break;
      case Integration.QUALYSVMDR:
        this.placeholder = this.qualysVMDRPlaceholder;
        break;
      case Integration.PRISMACLOUD:
        this.placeholder = this.prismacloudPlaceholder;
        break;
      default:
        break;
    }
  }

  toggleTenableSwitch(view: View): void {
    this.currView = view;
  }

  updateExistingCredentials(existingIntegrationInfo: IntegrationCreds, formDataControl: any): void {
    if (this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) {
      existingIntegrationInfo.subscriptionId = formDataControl.subscriptionId.value;
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === ServiceName.AWSSECURITYHUB) {
      existingIntegrationInfo.roleArn = formDataControl.roleArn.value;
      existingIntegrationInfo.region = formDataControl.region.value;
      return;
    }

    if (this.serviceName === ServiceName.SNYK) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.orgId = formDataControl.orgId.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.INTUNE) {
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === Integration.SENTINEL) {
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.subscriptionId = formDataControl.subscriptionId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      existingIntegrationInfo.resourceGroupName = formDataControl.resourceGroupName.value;
      existingIntegrationInfo.workspaceName = formDataControl.workspaceName.value;
      return;
    }

    if (this.serviceName === ServiceName.SENTINELONE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      return;
    }

    if (this.serviceName === ServiceName.WIZ) {
      existingIntegrationInfo.apiEndpointUrl = formDataControl.apiEndpointUrl.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === ServiceName.SPLUNK) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.currView = this.currView;
      if (this.currView === CURR_VIEW.USERNAME) {
        existingIntegrationInfo.username = formDataControl.username.value;
        existingIntegrationInfo.password = formDataControl.password.value;
      } else {
        existingIntegrationInfo.bearerToken = formDataControl.bearerToken.value;
      }
      return;
    }

    if (this.serviceName === ServiceName.OKTA) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      return;
    }

    if (this.serviceName === ServiceName.ORCA) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.GITHUBSECURITY) {
      existingIntegrationInfo.token = formDataControl.token.value;
      existingIntegrationInfo.organization = formDataControl.organization.value;
      return;
    }

    if (this.serviceName === ServiceName.GITLAB) {
      existingIntegrationInfo.token = formDataControl.token.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.VERACODE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiId = formDataControl.apiId.value;
      existingIntegrationInfo.apiKey = formDataControl.apiKey.value;
      return;
    }

    if (this.serviceName === ServiceName.SONARQUBE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.token = formDataControl.token.value;
      return;
    }

    if (this.serviceName === ServiceName.KNOWBE4) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.region = formDataControl.region.value;
      return;
    }

    if (this.serviceName === ServiceName.GCPSCC) {
      existingIntegrationInfo.serviceAccountKey = formDataControl.serviceAccountKey.value;
      existingIntegrationInfo.organizationId = formDataControl.organizationId.value;
      return;
    }

    if (this.serviceName === ServiceName.SERVICENOWCMDB) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.username = formDataControl.username.value;
      existingIntegrationInfo.password = formDataControl.password.value;
      return;
    }

    if (!UtilsService.isDefined(existingIntegrationInfo.currView) || this.currView === CURR_VIEW.USERNAME) {
      existingIntegrationInfo.username = formDataControl.username.value;
      existingIntegrationInfo.password = formDataControl.password.value;
    } else {
      existingIntegrationInfo.accessKey = formDataControl.accessKey.value;
      existingIntegrationInfo.secretKey = formDataControl.secretKey.value;
    }
    existingIntegrationInfo.url = formDataControl.url.value;
    existingIntegrationInfo.currView = this.currView;

    // Add product selection for Qualys
    if (
      (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) &&
      formDataControl?.product?.value
    ) {
      existingIntegrationInfo.product = formDataControl.product.value;
    }
  }

  async updateCredentials(
    existingCredentials: IntegrationCreds,
    existingItemIndex: number,
    integrationArray: IntegrationModal[]
  ): Promise<void> {
    if (existingCredentials?.currView !== this.currView) {
      existingCredentials.currView = this.currView;
      integrationArray[existingItemIndex] = this.setIntegrationInfo(existingCredentials);
      await this.entityService.updateEntity({
        id: this.entity.id,
        integrations: integrationArray,
      });
    }
  }

  isDefenderFormValid(credentials): boolean {
    if (
      credentials?.subscriptionId?.value &&
      credentials?.tenantId?.value &&
      credentials?.clientId?.value &&
      credentials?.clientSecret?.value
    ) {
      return true;
    }

    return false;
  }

  isAwsFormValid(credentials): boolean {
    if (credentials?.roleArn?.value && credentials?.region?.value) {
      return true;
    }

    return false;
  }

  isIntuneFormValid(credentials): boolean {
    if (credentials?.tenantId?.value && credentials?.clientId?.value && credentials?.clientSecret?.value) {
      return true;
    }

    return false;
  }

  isSentinelFormValid(credentials): boolean {
    if (
      credentials?.tenantId?.value &&
      credentials?.subscriptionId?.value &&
      credentials?.clientId?.value &&
      credentials?.clientSecret?.value &&
      credentials?.resourceGroupName?.value &&
      credentials?.workspaceName?.value
    ) {
      return true;
    }

    return false;
  }

  isWizFormValid(credentials): boolean {
    if (credentials?.apiEndpointUrl?.value && credentials?.clientId?.value && credentials?.clientSecret?.value) {
      return true;
    }

    return false;
  }

  isSplunkFormValid(credentials): boolean {
    if (!credentials?.url?.value) {
      return false;
    }
    if (this.currView === CURR_VIEW.USERNAME) {
      return !!(credentials?.username?.value && credentials?.password?.value);
    }
    return !!credentials?.bearerToken?.value;
  }

  isOktaFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.apiToken?.value) {
      return true;
    }

    return false;
  }

  isOrcaFormValid(credentials): boolean {
    if (credentials?.apiToken?.value && credentials?.url?.value) {
      return true;
    }

    return false;
  }

  isGithubSecurityFormValid(credentials): boolean {
    if (credentials?.token?.value && credentials?.organization?.value) {
      return true;
    }

    return false;
  }

  isGitlabFormValid(credentials): boolean {
    if (credentials?.token?.value && credentials?.url?.value) {
      return true;
    }

    return false;
  }

  isVeracodeFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.apiId?.value && credentials?.apiKey?.value) {
      return true;
    }

    return false;
  }

  isDuoFormValid(credentials): boolean {
    if (credentials?.apiHostname?.value && credentials?.integrationKey?.value && credentials?.secretKey?.value) {
      return true;
    }

    return false;
  }

  isSonarqubeFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.token?.value) {
      return true;
    }

    return false;
  }

  isKnowbe4FormValid(credentials): boolean {
    if (credentials?.apiToken?.value && credentials?.region?.value) {
      return true;
    }

    return false;
  }

  isGcpsccFormValid(credentials): boolean {
    if (credentials?.serviceAccountKey?.value && credentials?.organizationId?.value) {
      return true;
    }

    return false;
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
      .replace(/^(.)/, char => char.toUpperCase());
  }

  async downloadGuide() {
    let recurringInterval: any = null;
    try {
      this.toastrService.info(`Downloading ${this.toTitleCase(this.serviceName)} guide it might take a few moments...`);

      recurringInterval = setInterval(() => {
        this.toastrService.info(`Downloading ${this.toTitleCase(this.serviceName)} guide, please wait...`);
      }, 20000);

      const url = await this.fileService.getPresignedS3DownloadUrl(
        `public/RELATIONS/INTEGRATIONS/INTEGRATION_GUIDES/${this.serviceName.toUpperCase()}_GUIDE.pdf`
      );

      const file = await fetch(url);

      if (file.status === 200) {
        const blob = await file.blob();
        clearInterval(recurringInterval);
        recurringInterval = null;
        this.toastrService.clear();
        saveAs(blob, `${this.toTitleCase(this.serviceName)}Guide.pdf`);
        this.toastrService.success(`${this.toTitleCase(this.serviceName)} Guide Downloaded Successfully`);
      }
    } catch (error) {
      this.toastrService.clear();
      this.toastrService.error(`Error downloading ${this.toTitleCase(this.serviceName)} guide`);
    } finally {
      if (recurringInterval) {
        clearInterval(recurringInterval);
      }
    }
  }
}
